import { PromptElementImplInterface } from './PromptElementImplInterface';
import { PromptElementController, DataStateChangeEvent, RadioBoxList } from '@sas-dvr/ltjs-bird';
import { ReportState } from '@sas-dvr/internal-va-react-core/store/rootReducer';
import { getFormattedData } from './utils';
import { RadioListState } from '@sas-dvr/internal-va-react-core/store/birdElement/radioListState';
import {
  filter,
  tap,
  ignoreElements,
  map,
  mergeMap,
  distinctUntilChanged,
  skipWhile,
} from 'rxjs/operators';
import { Observable, EMPTY, of } from 'rxjs';
import { PortableEvent } from '@sas-dvr/ltjs-commons';
import { StateObservable } from 'redux-observable';
import { AnyAction } from 'redux';

export class RadioListImpl implements PromptElementImplInterface {
  private _controller: PromptElementController;
  private _id: string;
  private _searchable: boolean;
  private _ignoreValueChange: boolean = false;

  constructor(controller: PromptElementController, id: string, specialization: RadioBoxList) {
    this._controller = controller;
    this._id = id;
    this._searchable = specialization.getSearchable();
  }

  createElement() {
    const indices = this._controller.getCurrentValuesInTermsOfOrderedUniqueRowIndices();

    const data = getFormattedData(this._controller);

    return RadioListState.actions.add(this._id, {
      name: data.name,
      valueColumnLabel: data.valueColumnLabel,
      measureColumnLabel: data.measureColumnLabel,
      values: data.values,
      measureValues: data.measureValues,
      selectedIndex: indices ? indices[0] : 0,
      searchable: this._searchable,
    });
  }

  deleteElement() {
    return RadioListState.actions.delete(this._id);
  }

  handleDataChange(dataChange$: Observable<DataStateChangeEvent>) {
    return dataChange$.pipe(
      map(() => {
        const indices = this._controller.getCurrentValuesInTermsOfOrderedUniqueRowIndices();
        const data = getFormattedData(this._controller);
        return RadioListState.actions.update(this._id, {
          valueColumnLabel: data.valueColumnLabel,
          measureColumnLabel: data.measureColumnLabel,
          values: data.values,
          measureValues: data.measureValues,
          selectedIndex: indices ? indices[0] : 0,
        });
      })
    );
  }

  handleValueChanged(valueChange$: Observable<PortableEvent>) {
    return valueChange$.pipe(
      mergeMap(() => {
        if (this._ignoreValueChange) {
          return EMPTY;
        }
        const indices = this._controller.getCurrentValuesInTermsOfOrderedUniqueRowIndices();
        return of(
          RadioListState.actions.update(this._id, {
            selectedIndex: indices ? indices[0] : 0,
          })
        );
      })
    );
  }

  handleActions(_myActions$: Observable<AnyAction>, state$: StateObservable<ReportState>) {
    return state$.pipe(
      map((aState) => aState.birdElements.RadioList[this._id]),
      filter((myState): myState is Exclude<typeof myState, undefined> => myState !== undefined),
      map((myState) => myState.selectedIndex),
      distinctUntilChanged(),
      skipWhile((index) => index === 0),
      tap((selectedIndex) => {
        this._ignoreValueChange = true;
        this._controller.setCurrentValuesInTermsOfOrderedUniqueRowIndices([selectedIndex]);
        this._ignoreValueChange = false;
      }),
      ignoreElements()
    );
  }
}
